import { CountryLabelFor } from './types'

/**
 * Country names typed into a county field (DBS-322).
 *
 * About 5% of applications had "England", "United Kingdom", "UK" and friends as a county, and applicants born abroad
 * often repeat the country ("INDIA" with Birth Country India). A country is never a valid county, so these are
 * refused with a message saying what to enter instead. Genuine regions (Punjab, Lagos) and a blank county stay
 * allowed.
 *
 * Two checks:
 * - the UK and its nations, always, whatever country the address or birthplace is in;
 * - a county that just repeats the country chosen alongside it. This needs the country's name, which each repo
 *   supplies (countryLabelFor) from its own country list.
 */

/** Never a county: the UK and its nations (compared after normalising) */
export const UK_COUNTRY_NAMES = [
  'ENGLAND', 'ENG', 'WALES', 'SCOTLAND', 'NORTHERN IRELAND', 'N IRELAND',
  'UK', 'UNITED KINGDOM', 'GB', 'GREAT BRITAIN', 'BRITAIN',
]

/** Upper-case, dots dropped ("U.K." -> "UK"), runs of spaces collapsed */
const norm = (value: string | null | undefined): string =>
  (value ?? '').toUpperCase().replace(/\./g, '').replace(/\s+/g, ' ').trim()

/**
 * The names a country list label stands for, e.g.
 * "Korea, Republic of" -> KOREA REPUBLIC OF, KOREA; "United States of America (USA)" -> ... , USA
 */
export const countryNamesFromLabel = (label: string | undefined): string[] => {
  if (!label) return []
  const names = new Set<string>()
  names.add(norm(label.replace(/,/g, ' ')))
  names.add(norm(label.split(/[,(;]/)[0]))
  const bracketed = label.match(/\(([^)]+)\)/)
  if (bracketed) names.add(norm(bracketed[1]))
  names.delete('')
  return Array.from(names)
}

export type CountyCountryProblem = { kind: 'uk' | 'repeat', typed: string }

/**
 * Why a county value is really a country, or null when it is fine.
 * @param countryCode the country chosen alongside this county (address CountryCode / BirthCountry)
 */
export const countyCountryProblemKind = (county: string | null | undefined, countryCode: string | null | undefined, countryLabelFor: CountryLabelFor | undefined): CountyCountryProblem | null => {
  const value = norm(county)
  if (value.length === 0) return null
  const typed = (county ?? '').trim()
  if (UK_COUNTRY_NAMES.includes(value)) return { kind: 'uk', typed }
  if (countryCode && countryLabelFor && countryNamesFromLabel(countryLabelFor(countryCode)).includes(value)) return { kind: 'repeat', typed }
  return null
}
